const CONFIG_KEY = '__AUDIRIE_RUNTIME_CONFIG__';

const getRuntimeConfig = () => {
  const config = window[CONFIG_KEY];
  if (
    !config ||
    typeof config !== 'object' ||
    !config.publicOrigin ||
    !config.capabilities ||
    (
      config.capabilities.identity !== false &&
      (!config.firebase?.apiKey || !config.firebase?.projectId)
    )
  ) {
    throw new Error('Audirie runtime configuration was not loaded');
  }
  return config;
};

const isLocalDevelopment = () =>
  getRuntimeConfig().deploymentTier === 'development';

const LOCAL_DIAGNOSTIC_HOSTS = new Set(['localhost', '127.0.0.1', '::1', '[::1]']);

/**
 * Developer-only diagnostics on screen. `development` is a plain local run.
 * Isolated environments (Safe Dev and friends) report the `preview` tier, so
 * gating on `development` alone hides these where they are most needed —
 * `preview` therefore qualifies only when the page is served from a local
 * address. A hosted preview on a real domain, staging and production never
 * show them.
 */
const showDeveloperDiagnostics = () => {
  try {
    const tier = getRuntimeConfig().deploymentTier;
    if (tier === 'development') return true;
    if (tier !== 'preview') return false;
    return LOCAL_DIAGNOSTIC_HOSTS.has(String(window.location?.hostname || ''));
  } catch (err) {
    return false;
  }
};

const DIAGNOSTICS_LATCH_KEY = 'audirieDiagnostics';

/**
 * Being allowed to show diagnostics is not a reason to. Safe Dev is where the
 * product is reviewed, not only debugged, so the detail stays off until
 * somebody asks for it with ?diagnostics=1, and off again with ?diagnostics=0.
 *
 * The learner app rewrites the address bar to "/" shortly after it boots, so
 * the query string is only there to be read while this module is loading —
 * hence the latch, and hence sessionStorage, which also keeps the setting
 * through the in-app navigation that drops it. Storage is best effort: a
 * private window that throws just leaves diagnostics off.
 */
const latchDiagnosticsFromLocation = () => {
  try {
    const requested = new URLSearchParams(window.location?.search || '').get('diagnostics');
    if (requested !== '1' && requested !== '0') return;
    if (requested === '1') window.sessionStorage.setItem(DIAGNOSTICS_LATCH_KEY, '1');
    else window.sessionStorage.removeItem(DIAGNOSTICS_LATCH_KEY);
  } catch (err) { /* no window, private windows and blocked site data throw */ }
};

latchDiagnosticsFromLocation();

const diagnosticsRequested = () => {
  try {
    return window.sessionStorage.getItem(DIAGNOSTICS_LATCH_KEY) === '1';
  } catch (err) {
    return false;
  }
};

const DEFAULT_PROFILE_TIMEOUT_MS = 10000;

/**
 * How long the dashboard waits for /api/profile. Served per deployment tier
 * (see `profileTimeoutMs` in configs/runtime.js) because an isolated
 * environment far from its database needs far longer than production.
 */
const getProfileTimeoutMs = () => {
  try {
    const timeout = getRuntimeConfig().profileTimeoutMs;
    return Number.isInteger(timeout) && timeout > 0 ? timeout : DEFAULT_PROFILE_TIMEOUT_MS;
  } catch (err) {
    return DEFAULT_PROFILE_TIMEOUT_MS;
  }
};

const getFirebaseClientConfig = () => {
  const runtime = getRuntimeConfig();
  if (!runtime.capabilities.identity || !runtime.firebase?.enabled) {
    throw new Error('Firebase identity is disabled');
  }
  const firebase = runtime.firebase;
  return Object.fromEntries(
    Object.entries({
      apiKey: firebase.apiKey,
      authDomain: firebase.authDomain,
      projectId: firebase.projectId,
      storageBucket: firebase.storageBucket,
      messagingSenderId: firebase.messagingSenderId,
      appId: firebase.appId,
      measurementId: firebase.measurementId,
    }).filter(([, value]) => value != null && value !== '')
  );
};

module.exports = {
  getFirebaseClientConfig,
  getProfileTimeoutMs,
  getRuntimeConfig,
  diagnosticsRequested,
  isLocalDevelopment,
  latchDiagnosticsFromLocation,
  showDeveloperDiagnostics,
};
